import React from 'react';
import { useNode } from '@craftjs/core';
import { useTheme } from '../../../contexts/ThemeContext';

const fontFamilies = [
  { value: 'inherit', label: 'Default' },
  { value: 'Play, sans-serif', label: 'Play' },
  { value: 'Unbounded, sans-serif', label: 'Unbounded' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Courier New, monospace', label: 'Courier' },
];

const backgroundColors = [
  { value: 'transparent', label: 'None', lightBg: 'bg-transparent', darkBg: 'bg-transparent' },
  { value: '#A2FF00', label: 'Accent', lightBg: 'bg-[#A2FF00]', darkBg: 'bg-[#A2FF00]' },
  { value: '#f3f4f6', label: 'Light Gray', lightBg: 'bg-gray-100', darkBg: 'bg-zinc-800' },
  { value: '#1f2937', label: 'Dark Gray', lightBg: 'bg-gray-800', darkBg: 'bg-zinc-700' },
  { value: '#fef3c7', label: 'Yellow', lightBg: 'bg-amber-100', darkBg: 'bg-amber-900/30' },
  { value: '#dbeafe', label: 'Blue', lightBg: 'bg-blue-100', darkBg: 'bg-blue-900/30' },
  { value: '#dcfce7', label: 'Green', lightBg: 'bg-green-100', darkBg: 'bg-green-900/30' },
];

export const TextBlock = ({ text, fontSize, textAlign, fontWeight, fontFamily, backgroundColor, textColor }) => {
  const { isDark } = useTheme();
  const { connectors: { connect, drag }, selected, actions: { setProp } } = useNode((state) => ({
    selected: state.events.selected
  }));

  // Get responsive background color
  const getBgStyle = () => {
    if (!backgroundColor || backgroundColor === 'transparent') return {};
    // For dark mode, adjust certain colors
    if (isDark && backgroundColor === '#f3f4f6') return { backgroundColor: '#27272a' };
    if (isDark && backgroundColor === '#fef3c7') return { backgroundColor: 'rgba(180, 83, 9, 0.3)' };
    if (isDark && backgroundColor === '#dbeafe') return { backgroundColor: 'rgba(30, 58, 138, 0.3)' };
    if (isDark && backgroundColor === '#dcfce7') return { backgroundColor: 'rgba(20, 83, 45, 0.3)' };
    return { backgroundColor };
  };

  return (
    <div
      ref={(ref) => ref && connect(drag(ref))}
      className={`p-4 rounded-lg transition-all ${
        selected ? 'ring-2 ring-[#A2FF00]' : ''
      } ${isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-gray-100'}`}
      style={getBgStyle()}
    >
      <p
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => setProp(props => props.text = e.target.innerText)}
        className={`outline-none`}
        style={{
          fontSize: fontSize || '16px',
          textAlign: textAlign || 'left',
          fontWeight: fontWeight || 'normal',
          fontFamily: fontFamily || 'inherit',
          color: textColor || (isDark ? '#e4e4e7' : '#374151')
        }}
      >
        {text}
      </p>
    </div>
  );
};

TextBlock.craft = {
  displayName: 'Text',
  props: {
    text: 'Click to edit text...',
    fontSize: '16px',
    textAlign: 'left',
    fontWeight: 'normal',
    fontFamily: 'inherit',
    backgroundColor: 'transparent',
    textColor: ''
  },
  related: {
    toolbar: TextBlockSettings
  }
};

function TextBlockSettings() {
  const { actions: { setProp }, props } = useNode((node) => ({
    props: node.data.props
  }));
  const { isDark } = useTheme();

  return (
    <div className="space-y-4">
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Font Family
        </label>
        <select
          value={props.fontFamily || 'inherit'}
          onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
        >
          {fontFamilies.map((font) => (
            <option key={font.value} value={font.value}>{font.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Font Size
        </label>
        <select
          value={props.fontSize}
          onChange={(e) => setProp(props => props.fontSize = e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
        >
          <option value="14px">Small</option>
          <option value="16px">Normal</option>
          <option value="18px">Large</option>
          <option value="20px">X-Large</option>
        </select>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Alignment
        </label>
        <div className="flex gap-2">
          {['left', 'center', 'right'].map((align) => (
            <button
              key={align}
              onClick={() => setProp(props => props.textAlign = align)}
              className={`flex-1 px-3 py-2 rounded-lg border capitalize ${
                props.textAlign === align
                  ? 'bg-[#A2FF00] text-black border-[#A2FF00]'
                  : isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {align}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Weight
        </label>
        <div className="flex gap-2">
          {['normal', 'bold'].map((weight) => (
            <button
              key={weight}
              onClick={() => setProp(props => props.fontWeight = weight)}
              className={`flex-1 px-3 py-2 rounded-lg border capitalize ${
                props.fontWeight === weight
                  ? 'bg-[#A2FF00] text-black border-[#A2FF00]'
                  : isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {weight}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Background Color
        </label>
        <div className="grid grid-cols-4 gap-2">
          {backgroundColors.map((color) => (
            <button
              key={color.value}
              onClick={() => setProp(props => props.backgroundColor = color.value)}
              className={`p-2 rounded-lg border flex items-center justify-center ${
                props.backgroundColor === color.value
                  ? 'ring-2 ring-[#A2FF00]'
                  : ''
              } ${isDark ? 'border-zinc-700' : 'border-gray-300'}`}
              title={color.label}
            >
              <div 
                className={`w-6 h-6 rounded ${color.value === 'transparent' ? 'border border-dashed' : ''}`}
                style={{ backgroundColor: color.value === 'transparent' ? 'transparent' : color.value }}
              />
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Text Color
        </label>
        <div className="flex gap-2">
          <input
            type="color"
            value={props.textColor || (isDark ? '#e4e4e7' : '#374151')}
            onChange={(e) => setProp(props => props.textColor = e.target.value)}
            className="w-10 h-10 rounded cursor-pointer"
          />
          <button
            onClick={() => setProp(props => props.textColor = '')}
            className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
          >
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
}

export { TextBlockSettings };